// 发型库与提示词，前端和 Worker 共用：前端展示名称，Worker 负责拼最终提示词。

export type Gender = "female" | "male";
/** 分类：女款按长度，男款按常见叫法 */
export type Category = "short" | "medium" | "long" | "curly" | "updo" | "buzz" | "back" | "fringe" | "perm";
/** 占位剪影，用于还没生成过的发型卡片 */
export type Silhouette = "pixie" | "bob" | "lob" | "long" | "curly" | "bun" | "buzz" | "crop" | "swept" | "wolf";

export interface HairStyle {
  id: string;
  gender: Gender;
  name: string;
  /** 一句话说明，展示在卡片上 */
  tip: string;
  category: Category;
  silhouette: Silhouette;
  /** 发给模型的英文描述 */
  prompt: string;
}

export const STYLES: HairStyle[] = [
  // ---------- 女款 ----------
  {
    id: "f-lob",
    gender: "female",
    name: "锁骨发",
    tip: "内扣微层次，百搭不挑脸",
    category: "medium",
    silhouette: "lob",
    prompt: "a collarbone-length lob with soft face-framing layers, ends gently curved inward",
  },
  {
    id: "f-french-bob",
    gender: "female",
    name: "法式刘海短发",
    tip: "齐下巴 + 碎刘海，显脸小",
    category: "short",
    silhouette: "bob",
    prompt: "a chin-length French bob with light, wispy bangs that just graze the eyebrows",
  },
  {
    id: "f-classic-bob",
    gender: "female",
    name: "经典波波头",
    tip: "齐耳干净利落",
    category: "short",
    silhouette: "bob",
    prompt: "a sleek jaw-length classic straight bob with blunt ends and a side part, no bangs",
  },
  {
    id: "f-curtain-long",
    gender: "female",
    name: "八字刘海长发",
    tip: "修饰额头和颧骨",
    category: "long",
    silhouette: "long",
    prompt: "long layered hair below the shoulders with soft curtain bangs parted in the middle",
  },
  {
    id: "f-sleek-long",
    gender: "female",
    name: "黑长直",
    tip: "中分顺直，气质款",
    category: "long",
    silhouette: "long",
    prompt: "long sleek straight hair reaching mid-back with a clean center part, smooth and glossy",
  },
  {
    id: "f-air-bangs",
    gender: "female",
    name: "空气刘海中长发",
    tip: "轻薄刘海，减龄",
    category: "medium",
    silhouette: "lob",
    prompt: "shoulder-length hair with airy see-through Korean-style bangs and slightly flipped ends",
  },
  {
    id: "f-s-wave",
    gender: "female",
    name: "韩系水波纹",
    tip: "自然 S 纹，温柔",
    category: "medium",
    silhouette: "curly",
    prompt: "medium-long hair with soft Korean-style S-shaped water waves, light and airy volume",
  },
  {
    id: "f-big-waves",
    gender: "female",
    name: "大波浪",
    tip: "蓬松大卷，成熟",
    category: "long",
    silhouette: "curly",
    prompt: "long hair with big loose glamorous waves, voluminous, reaching past the shoulders",
  },
  {
    id: "f-wool-perm",
    gender: "female",
    name: "羊毛卷",
    tip: "细密小卷，蓬松感",
    category: "curly",
    silhouette: "curly",
    prompt: "a shoulder-length voluminous wool-roll perm made of small tight bouncy curls",
  },
  {
    id: "f-butterfly",
    gender: "female",
    name: "蝴蝶剪",
    tip: "大层次，蓬松灵动",
    category: "long",
    silhouette: "long",
    prompt: "a butterfly haircut: long hair with short face-framing layers and bouncy blow-dried volume",
  },
  {
    id: "f-wolf",
    gender: "female",
    name: "狼尾",
    tip: "个性层次，酷一点",
    category: "medium",
    silhouette: "wolf",
    prompt: "a medium-length wolf cut with shaggy choppy layers on top and longer wispy ends at the nape",
  },
  {
    id: "f-hime",
    gender: "female",
    name: "公主切",
    tip: "侧边齐切，日系",
    category: "long",
    silhouette: "long",
    prompt: "a hime cut: long straight hair with blunt bangs and cheek-length blunt side locks framing the face",
  },
  {
    id: "f-pixie",
    gender: "female",
    name: "精灵短发",
    tip: "超短清爽，很挑脸",
    category: "short",
    silhouette: "pixie",
    prompt: "a short textured pixie cut with slightly longer swept bangs, ears visible",
  },
  {
    id: "f-inward-short",
    gender: "female",
    name: "蛋卷短发",
    tip: "蓬松内扣，温柔短发",
    category: "short",
    silhouette: "bob",
    prompt: "a short bob with airy volume and soft inward-curled ends, rounded silhouette, light bangs",
  },
  {
    id: "f-high-bun",
    gender: "female",
    name: "丸子头",
    tip: "露额头，精神",
    category: "updo",
    silhouette: "bun",
    prompt: "all hair gathered into a neat high bun on top of the head, a few loose face-framing strands",
  },
  {
    id: "f-low-pony",
    gender: "female",
    name: "低马尾",
    tip: "干练通勤",
    category: "updo",
    silhouette: "bun",
    prompt: "hair pulled back into a sleek low ponytail at the nape with a center part",
  },
  {
    id: "f-short-curly",
    gender: "female",
    name: "短卷发",
    tip: "齐耳小卷，俏皮",
    category: "curly",
    silhouette: "curly",
    prompt: "ear-length short curly hair with defined springy curls and rounded volume",
  },
  {
    id: "f-braid",
    gender: "female",
    name: "侧编发",
    tip: "温柔，适合约会",
    category: "updo",
    silhouette: "long",
    prompt: "a loose romantic side braid falling over one shoulder with soft face-framing strands",
  },

  // ---------- 男款 ----------
  {
    id: "m-buzz",
    gender: "male",
    name: "寸头",
    tip: "最利落，看头型",
    category: "buzz",
    silhouette: "buzz",
    prompt: "a very short uniform buzz cut, about 3 mm all over",
  },
  {
    id: "m-crew",
    gender: "male",
    name: "圆寸",
    tip: "寸头稍长，更柔和",
    category: "buzz",
    silhouette: "buzz",
    prompt: "a short crew cut with slightly longer top (about 1.5 cm) tapered on the sides, rounded shape",
  },
  {
    id: "m-fade-crop",
    gender: "male",
    name: "美式渐变",
    tip: "两侧渐变，顶部纹理",
    category: "short",
    silhouette: "crop",
    prompt: "a textured crop on top with a mid skin fade on the sides and back",
  },
  {
    id: "m-french-crop",
    gender: "male",
    name: "碎盖",
    tip: "前额碎刘海，显年轻",
    category: "short",
    silhouette: "crop",
    prompt: "a messy French crop with a short textured fringe falling forward onto the forehead, tapered sides",
  },
  {
    id: "m-side-part",
    gender: "male",
    name: "韩式二八分",
    tip: "蓬松侧分，斯文",
    category: "fringe",
    silhouette: "swept",
    prompt: "a Korean-style 2:8 side part with soft volume on top swept to one side, neat tapered sides",
  },
  {
    id: "m-comma",
    gender: "male",
    name: "逗号刘海",
    tip: "韩系，前额弯弧",
    category: "fringe",
    silhouette: "swept",
    prompt: "Korean comma hair: medium-length top with the front fringe curled inward like a comma over one eyebrow",
  },
  {
    id: "m-middle-part",
    gender: "male",
    name: "中分",
    tip: "帘式中分，文艺",
    category: "fringe",
    silhouette: "swept",
    prompt: "a curtain middle part for men, eyebrow-length fringe split in the center, soft and natural",
  },
  {
    id: "m-slick-back",
    gender: "male",
    name: "大背头",
    tip: "经典油头，成熟稳重",
    category: "back",
    silhouette: "swept",
    prompt:
      "a classic big slicked-back hairstyle: top hair about 8-10 cm long combed straight back with full volume and a slight lift at the front hairline, sides neatly tapered and combed back, groomed with a light natural shine",
  },
  {
    id: "m-side-slick",
    gender: "male",
    name: "三七分背头",
    tip: "侧分往后梳，商务",
    category: "back",
    silhouette: "swept",
    prompt:
      "a 3:7 side-parted slicked-back hairstyle: a clean side part, top hair combed back and slightly to one side with volume, short tapered sides, light natural shine",
  },
  {
    id: "m-business",
    gender: "male",
    name: "商务短发",
    tip: "干净利落，不出错",
    category: "short",
    silhouette: "crop",
    prompt:
      "a neat conservative business short haircut: short tapered sides and back, about 4 cm on top combed slightly to the side, clean and tidy",
  },
  {
    id: "m-quiff",
    gender: "male",
    name: "飞机头",
    tip: "前额竖起，精神",
    category: "back",
    silhouette: "swept",
    prompt: "a modern quiff with the front hair lifted up and back, short tapered sides",
  },
  {
    id: "m-classic-side",
    gender: "male",
    name: "绅士侧分",
    tip: "商务，干净",
    category: "back",
    silhouette: "swept",
    prompt: "a classic gentleman's side part with a clean defined part line, short neat sides",
  },
  {
    id: "m-texture-perm",
    gender: "male",
    name: "纹理烫",
    tip: "蓬松有层次",
    category: "perm",
    silhouette: "curly",
    prompt: "a men's textured perm with loose wavy texture and volume on top, tapered sides",
  },
  {
    id: "m-tin-perm",
    gender: "male",
    name: "锡纸烫",
    tip: "细密卷，街头感",
    category: "perm",
    silhouette: "curly",
    prompt: "a men's twist perm with tight small curls on top and short faded sides",
  },
  {
    id: "m-mullet",
    gender: "male",
    name: "狼尾",
    tip: "后长前短，个性",
    category: "medium",
    silhouette: "wolf",
    prompt: "a modern men's mullet wolf cut: short textured top and sides, longer layered hair at the back of the neck",
  },
  {
    id: "m-japanese",
    gender: "male",
    name: "日系中长发",
    tip: "盖耳，慵懒",
    category: "medium",
    silhouette: "lob",
    prompt: "medium-length Japanese-style layered men's hair covering the ears, soft and slightly messy",
  },
  {
    id: "m-man-bun",
    gender: "male",
    name: "男士丸子头",
    tip: "长发扎起",
    category: "medium",
    silhouette: "bun",
    prompt: "shoulder-length men's hair tied back into a casual man bun at the back of the head",
  },
  {
    id: "m-flat-top",
    gender: "male",
    name: "板寸",
    tip: "顶部平整，硬朗",
    category: "buzz",
    silhouette: "buzz",
    prompt: "a flat top haircut with a level flat top and very short sides",
  },
  {
    id: "m-curly-short",
    gender: "male",
    name: "自然卷短发",
    tip: "随性卷度",
    category: "perm",
    silhouette: "curly",
    prompt: "short natural curly hair with defined curls on top and trimmed sides",
  },
  {
    id: "m-shaved",
    gender: "male",
    name: "光头",
    tip: "看看头型",
    category: "buzz",
    silhouette: "buzz",
    prompt: "a completely clean-shaven bald head",
  },
];

export interface HairColor {
  id: string;
  name: string;
  /** 色块展示用 */
  swatch: string;
  /** null = 保持原发色 */
  prompt: string | null;
}

export const COLORS: HairColor[] = [
  { id: "keep", name: "原发色", swatch: "conic-gradient(#3a2e2a, #6b4e3d, #3a2e2a)", prompt: null },
  { id: "black", name: "自然黑", swatch: "#1f1c1b", prompt: "natural soft black" },
  { id: "dark-brown", name: "深棕", swatch: "#3d2a22", prompt: "natural dark brown" },
  { id: "chestnut", name: "栗棕", swatch: "#6a3f2a", prompt: "warm chestnut brown" },
  { id: "milk-tea", name: "奶茶棕", swatch: "#a07b62", prompt: "soft milk-tea brown (light beige brown)" },
  { id: "ash", name: "雾霾灰", swatch: "#7c7a78", prompt: "cool ash grey-brown" },
];

export const CATEGORY_LABELS: Record<Category, string> = {
  short: "短发",
  medium: "中长",
  long: "长发",
  curly: "卷发",
  updo: "扎发",
  buzz: "寸头",
  back: "背头侧分",
  fringe: "刘海",
  perm: "烫发",
};

const CATEGORY_ORDER: Record<Gender, Category[]> = {
  female: ["short", "medium", "long", "curly", "updo"],
  male: ["short", "back", "fringe", "buzz", "perm", "medium"],
};

/** 某个性别实际有发型的分类（空分类不展示） */
export function categoriesFor(gender: Gender): Category[] {
  return CATEGORY_ORDER[gender].filter((c) => STYLES.some((s) => s.gender === gender && s.category === c));
}

/** 自定义描述最大长度 */
export const CUSTOM_MAX = 120;

/** 改提示词时递增，保证旧缓存不会被误用 */
export const PROMPT_VERSION = 4;

export function findStyle(id: string): HairStyle | undefined {
  return STYLES.find((s) => s.id === id);
}

export function findColor(id: string): HairColor | undefined {
  return COLORS.find((c) => c.id === id);
}

export interface StyleRequest {
  styleId: string;
  colorId: string;
  /** styleId === "custom" 时使用 */
  custom?: string;
}

/**
 * 拼最终提示词。核心是：只改头发，脸、姿态、构图全部不动。
 * 就算模型偷偷改了脸，前端也会用原图像素把五官贴回去，这里是第一道防线。
 */
export function buildPrompt(req: StyleRequest): string {
  let hair: string;
  if (req.styleId === "custom") {
    const text = (req.custom ?? "").trim().slice(0, CUSTOM_MAX);
    if (!text) throw new Error("自定义发型描述为空");
    hair = text;
  } else {
    const style = findStyle(req.styleId);
    if (!style) throw new Error(`未知发型：${req.styleId}`);
    hair = style.prompt;
  }
  const color = findColor(req.colorId) ?? COLORS[0];
  const colorLine = color.prompt
    ? `Hair color: ${color.prompt}.`
    : "Keep the original hair color exactly as it is in the photo.";

  return [
    `Edit this photo: completely replace the person's current hairstyle with ${hair}.`,
    "Remove all of the old hair, including any strands that currently hang over the forehead, eyes or ears, unless the new style itself covers those areas.",
    colorLine,
    "The new hair must fit this person's real head: keep the skull shape and head size the same, put a natural hairline on the actual forehead, and blend naturally at the temples, sideburns and nape with realistic volume for this hairstyle.",
    "Keep the person's face completely unchanged: identical face shape, jawline, eyes, eyebrows, nose, lips, ears, skin tone, skin texture, expression and head pose.",
    "Do not beautify, slim, reshape or retouch the face. Do not add makeup, glasses or accessories.",
    "Keep the same clothing, background, lighting, camera angle and framing. Do not zoom, crop, rotate or shift the image.",
    "Photorealistic photograph: real hair strands with natural shine and lighting that matches the photo. It must look like a real haircut, not a wig or a pasted-on hairpiece.",
  ].join(" ");
}

/** 结果缓存用的稳定 key（不含图片哈希） */
export function styleKey(req: StyleRequest): string {
  const custom = req.styleId === "custom" ? `:${(req.custom ?? "").trim().slice(0, CUSTOM_MAX)}` : "";
  return `v${PROMPT_VERSION}:${req.styleId}:${req.colorId}${custom}`;
}

/** 发型示例图（一次性生成的真人示意，不是家人照片） */
export const SAMPLE_VERSION = 1;

export function buildSamplePrompt(style: HairStyle): string {
  const who = style.gender === "male" ? "a Chinese man in his early 30s" : "a Chinese woman in her late 20s";
  return [
    `Photorealistic studio portrait photo of ${who} with ${style.prompt}.`,
    "Front-facing, looking at the camera, relaxed neutral expression, head and shoulders framing with the whole hairstyle visible.",
    "Plain light warm-gray background, soft even studio lighting, natural skin texture, wearing a plain white crew-neck T-shirt.",
    "The hairstyle is neat, clearly visible and realistic, like a salon lookbook photo. No text, no watermark, no accessories.",
  ].join(" ");
}
